/**
 * Écran « Hypothèses » (ADR 0011) : fiche de chaque paramètre du moteur, fusion de `content/params.json` (valeur, libellé,
 * fourchette) et de `content/params.sources.json` (contrat v0.3 supposé, détecté à l'exécution :
 * `{ [param_id]: { low?, high?, source?, note?, status?: "parametre_de_jeu", influential? } }`).
 * Aucun chiffre ici : seulement des identifiants (vocabulaire) et des règles de regroupement.
 */
import type { Param, ParamSet, Source } from "../../engine/src/types.js";

/** Statut d'affichage d'un paramètre. */
export type ParamStatus = "sourced" | "game" | "todo";

export interface ParamSourceEntry {
  low?: number;
  high?: number;
  source?: Source | Source[] | "TODO";
  note?: string;
  status?: string;
  influential?: boolean;
}

export interface ParamSources {
  entries: Record<string, ParamSourceEntry>;
  /** Paramètres déclarés influents par le fichier (liste `_influential` / `influential` ou drapeau par entrée). */
  influential: Set<string>;
}

/** Sensibilité exportée (optionnelle) : `content/params.sensitivity.json` `{ id: { score } }` ou `[{ id, score }]`. */
export type ParamSensitivity = Record<string, { score: number }>;

export interface ParamInfo {
  id: string;
  label: string;
  value: number | null;
  default: number | null;
  unit: Param["unit"];
  low?: number;
  high?: number;
  sources: Source[];
  status: ParamStatus;
  notes: string[];
  influential: boolean;
  group: string;
  sensitivity?: number;
}

const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === "object" && !Array.isArray(x);
const isNum = (x: unknown): x is number => typeof x === "number" && Number.isFinite(x);

function asSources(x: unknown): Source[] {
  const one = (s: unknown): Source | null => (isObj(s) && typeof s.url === "string" && typeof s.title === "string" ? (s as unknown as Source) : null);
  if (Array.isArray(x)) return x.map(one).filter((s): s is Source => !!s);
  const s = one(x);
  return s ? [s] : [];
}

/** Lecture défensive de `params.sources.json` ; null si le fichier est absent ou n'a pas la forme attendue. */
export function parseParamSources(raw: unknown): ParamSources | null {
  if (!isObj(raw)) return null;
  const entries: Record<string, ParamSourceEntry> = {};
  const influential = new Set<string>();
  for (const key of ["_influential", "influential"]) {
    const list = raw[key];
    if (Array.isArray(list)) for (const id of list) if (typeof id === "string") influential.add(id);
  }
  for (const [id, v] of Object.entries(raw)) {
    if (id.startsWith("_") || !isObj(v)) continue;
    const e: ParamSourceEntry = {};
    if (isNum(v.low)) e.low = v.low;
    if (isNum(v.high)) e.high = v.high;
    if (v.source !== undefined) e.source = v.source as NonNullable<ParamSourceEntry["source"]>;
    else if (Array.isArray(v.sources) && v.sources.length) e.source = v.sources as Source[];
    if (typeof v.note === "string") e.note = v.note;
    if (typeof v.status === "string") e.status = v.status;
    if (v.influential === true) {
      e.influential = true;
      influential.add(id);
    }
    entries[id] = e;
  }
  return { entries, influential };
}

export function parseSensitivity(raw: unknown): ParamSensitivity | null {
  const out: ParamSensitivity = {};
  if (Array.isArray(raw)) {
    for (const r of raw) if (isObj(r) && typeof r.id === "string" && isNum(r.score)) out[r.id] = { score: r.score };
  } else if (isObj(raw)) {
    for (const [id, r] of Object.entries(raw)) if (isObj(r) && isNum(r.score)) out[id] = { score: r.score };
  }
  return Object.keys(out).length ? out : null;
}

/** Groupes de l'écran, dans l'ordre d'affichage (libellé, motif sur l'identifiant). Le premier qui correspond gagne. */
export const PARAM_GROUPS: Array<[string, RegExp]> = [
  ["Règles du jeu : mandat ; ancien verdict et score (non utilisés depuis le bilan chiffré)", /^(mandate_years|deficit_rule|verdict_|reelection_|score_|confidence_(low|high)$|game_over_)/],
  ["Multiplicateurs budgétaires", /^(mult_|impulse_)/],
  ["Recettes et soldes publics", /^(revenue_|cyclical_|asso_|apul_)/],
  ["Taux d'intérêt, inflation et dette", /^(spread_|oat_|infl_|state_share_of_apu_interest)/],
  ["Croissance et emploi", /^(gpot_|okun_|output_gap_|unemployment_|trade_|mission_trend_|other_spend_)/],
  ["Confiance des marchés et Union européenne", /^(conf_|fx_conf_|fx_eu_|eu_)/],
  ["Soutien politique, majorité et cohésion", /^(coh_|pop_|capital_|political_|pol_|support_|maj_|majority_|censure_)/],
  ["Leviers de financement (impôts)", /^lev_/],
  ["Économies (rabot et missions)", /^cut_/],
  ["Prix et pouvoir d'achat", /^(pt_|price_|pp_)/],
  ["Services publics", /^(svcind_|svc_|stars_)/],
  ["Climat et pauvreté", /^(co2_|poverty_)/],
  ["Affichage, chiffrage IA et divers", /^(attribution_|estimate_|observatory_|journal_|leak_cap|default_seed)/],
];
export const OTHER_GROUP = "Autres paramètres";

export function paramGroup(id: string): string {
  return PARAM_GROUPS.find(([, re]) => re.test(id))?.[0] ?? OTHER_GROUP;
}

/** Statut « paramètre de jeu » déclaré par params.sources.json (non issu de la littérature). */
export const GAME_PARAM_STATUS = "parametre_de_jeu";
/** Statut porté par content/params.json pour les mécaniques sans littérature (ADR 0014 §4). */
export const CONVENTION_STATUS = "convention_de_jeu";

/**
 * Hypothèses que le joueur peut modifier (vocabulaire : identifiants seulement), regroupées. Bornes = fourchette de
 * params.sources.json, sinon celle de params.json ; sans fourchette, le curseur est désactivé.
 */
export const ADJUSTABLE_GROUPS: Array<{ label: string; ids: string[] }> = [
  { label: "Multiplicateurs budgétaires", ids: ["mult_spend_investment", "mult_spend_transfer", "mult_spend_payroll", "mult_measure_revenue", "mult_tax_households", "mult_tax_firms"] },
  { label: "Élasticité des recettes", ids: ["revenue_elasticity_gdp"] },
  { label: "Sensibilité des taux à la dette", ids: ["spread_per_debt_pt"] },
  { label: "Transmission de la TVA aux prix", ids: ["pt_tva_passthrough"] },
];
export const ADJUSTABLE_IDS: readonly string[] = ADJUSTABLE_GROUPS.flatMap((g) => g.ids);

/** Fiche d'un paramètre : valeur utilisée (éventuellement modifiée), valeur par défaut, fourchette, sources, statut. */
export function paramInfo(id: string, defaults: ParamSet, effective: ParamSet, ps: ParamSources | null, sens: ParamSensitivity | null): ParamInfo | null {
  const p = defaults[id];
  if (!p) return null;
  const e = ps?.entries[id];
  const fromFile = asSources(e?.source);
  const own = [...asSources(p.source), ...asSources((p as Param & { sources?: unknown }).sources)];
  const sources = fromFile.length ? fromFile : own;
  // `convention_de_jeu` (content/params.json, ADR 0014 §4) ou `parametre_de_jeu` (params.sources.json) : paramètre de jeu déclaré.
  const declaredGame = e?.status === GAME_PARAM_STATUS || e?.status === CONVENTION_STATUS || p.status === CONVENTION_STATUS;
  const status: ParamStatus = declaredGame && !sources.length ? "game" : sources.length ? "sourced" : "todo";
  const notes: string[] = [];
  if (e?.note) notes.push(e.note);
  if (p.note && p.note !== e?.note) notes.push(p.note);
  const low = e?.low ?? p.low;
  const high = e?.high ?? p.high;
  const s = sens?.[id]?.score;
  return {
    id,
    label: p.label,
    value: effective[id]?.value ?? p.value,
    default: p.value,
    unit: p.unit,
    ...(low !== undefined ? { low } : {}),
    ...(high !== undefined ? { high } : {}),
    sources,
    status,
    notes,
    influential: !!ps?.influential.has(id) || (p as Param & { influential?: boolean }).influential === true,
    group: paramGroup(id),
    ...(s !== undefined ? { sensitivity: s } : {}),
  };
}

export function allParamInfo(defaults: ParamSet, effective: ParamSet, ps: ParamSources | null, sens: ParamSensitivity | null): ParamInfo[] {
  return Object.keys(defaults)
    .map((id) => paramInfo(id, defaults, effective, ps, sens))
    .filter((x): x is ParamInfo => !!x);
}

/**
 * Le bandeau « prototype » peut-il disparaître ? Seulement si des paramètres sont déclarés influents ET que chacun a une
 * source publiée (un « paramètre de jeu » déclaré n'est pas une source). Aucun paramètre déclaré influent → jamais.
 */
export function influentialCovered(defaults: ParamSet, ps: ParamSources | null): boolean {
  const infos = allParamInfo(defaults, defaults, ps, null).filter((i) => i.influential);
  return infos.length > 0 && infos.every((i) => i.status === "sourced");
}

/** Bornes d'un curseur d'hypothèse ; null si le paramètre n'a pas de fourchette exploitable. */
export function adjustableRange(info: ParamInfo | null): { low: number; high: number } | null {
  if (!info || info.low === undefined || info.high === undefined || !(info.high > info.low)) return null;
  return { low: info.low, high: info.high };
}

/** Surcharges valides seulement : identifiant modifiable, valeur finie dans la fourchette (lien forgé → ignorée). */
export function sanitizeOverrides(raw: unknown, defaults: ParamSet, ps: ParamSources | null): Record<string, number> {
  const out: Record<string, number> = {};
  if (!isObj(raw)) return out;
  for (const [id, v] of Object.entries(raw)) {
    if (!ADJUSTABLE_IDS.includes(id) || !isNum(v)) continue;
    const r = adjustableRange(paramInfo(id, defaults, defaults, ps, null));
    if (!r || v < r.low || v > r.high) continue;
    if (v !== defaults[id]?.value) out[id] = v;
  }
  return out;
}

/** Jeu de paramètres effectif (défauts + surcharges du joueur). */
export function applyOverrides(defaults: ParamSet, overrides: Record<string, number>): ParamSet {
  if (!Object.keys(overrides).length) return defaults;
  const out: ParamSet = { ...defaults };
  for (const [id, v] of Object.entries(overrides)) if (out[id]) out[id] = { ...out[id]!, value: v };
  return out;
}
